// PickingScreen — unified hero for all 5 picking workflows.
//
// Renders the V4_ActionBar layout from
// design_handoff_picking_screen/source/variants.jsx for the active line.
// Both ActiveWaveScreen (Single / Pick & Sort / Large) and
// ActiveDropScreen (Shelf Drop / Forecast Drop) render this so the picker
// sees the same shape regardless of workflow.
//
// Purely presentational — the parent owns the phase machine, scan
// handling, and tote/wave state. PickingScreen reads from props and
// renders. The parent passes:
//   • current line data (location, SKU, qty, image, bay_position, etc.)
//   • the "scan target" — which area should pulse green next
//   • the picker's settings (show image, other-skus count)
//   • action handlers (pause / alert / back)
//
// The "scan target" indicator drives the green accent: when the next
// thing the picker needs to scan is the LOCATION (require_location_scan
// is on AND the wave is in awaiting-location), the location text + pin
// go green. Otherwise — barcode is next — the dark action bar's scan
// tile + barcode text go green. The action bar sits between the
// location hero and the product card; the bottom of the screen belongs
// to the green "last picked" banner once the first pick lands.
import { useEffect, useRef, useState } from "react";
import { MapPin, ScanLine, Package, Boxes, Hand, Plus } from "lucide-react";
import { useTranslation } from "../../i18n";
import { ScoreDisplay, TimeBonusPopup, type CycleScoreApi } from "../gamification";
import AlsoAtLocation, { type AlsoItem } from "./AlsoAtLocation";
import AlternateBarcodesModal from "./AlternateBarcodesModal";
import { groupBarcode } from "./barcodeFormat";
import BayDiagram from "./BayDiagram";
import AisleSummary from "./AisleSummary";
import type { AisleGroup } from "./aisleUnits";
import { stripWarehouse } from "./locationDisplay";
import PickingTopBar from "./PickingTopBar";
import FitWord from "../FitWord";
import { playSuccess } from "../helpers/audioPlayer";
import "./PickingScreen.css";

// ── Location-change transition timing ────────────────────────────────
// When the last unit at a location is picked and the next line is
// somewhere else, a full-screen takeover mounts for LOCATION_CHANGE_MS
// (overridable per warehouse via settings.picking_location_change_ms —
// the `locationChangeMs` prop), then a "new location" highlight state
// holds for NEW_LOCATION_HIGHLIGHT_MS as the safety net for a picker
// who missed the takeover window.
export const LOCATION_CHANGE_MS = 2000;
export const NEW_LOCATION_HIGHLIGHT_MS = 6000;

// The picker walked: which phase of the transition is on screen, and
// where they walked FROM (shown as "was A-3-3" so a picker standing in
// the wrong bay can self-diagnose). heroTop is the on-screen location
// hero's viewport offset, measured as the takeover mounts, so the
// takeover can render the new location code in the SAME spot the
// picker is already looking at.
interface LocationTransition {
    stage: "takeover" | "highlight";
    prevLocation: string;
    heroTop?: number;
}

// Product image with fallback to the striped placeholder when either
// no URL is set OR the URL is broken (404, network error, etc.). The
// useState tracks the last src that failed to load so the placeholder
// renders on the very next paint — re-mounting the <img> via `key`
// resets the failed flag when the picker advances to a different
// product with a fresh URL.
export const ProductImage = ({ thumbnailUrl, imageUrl }: { thumbnailUrl: string | null; imageUrl: string | null }) => {
    const src = thumbnailUrl || imageUrl;
    const [failedSrc, setFailedSrc] = useState<string | null>(null);
    if (!src || failedSrc === src) {
        return (
            <div className="ps-product-img ps-product-img--placeholder">
                <Package className="ps-product-img-fallback" strokeWidth={1} />
            </div>
        );
    }
    return (
        <div className="ps-product-img">
            <img
                key={src}
                src={src}
                alt=""
                loading="lazy"
                onError={() => setFailedSrc(src)}
            />
        </div>
    );
};

export type ScanTarget = "location" | "barcode";

export interface PickingScreenProps {
    // ── Wave/order context ─────────────────────────────────────────
    priority?: "low" | "medium" | "high" | null;
    /** Display label for the order pill — e.g. "#10007" or "wave 142". */
    orderRef: string;
    itemsTotal: number;
    itemsPicked: number;

    // ── Current line ───────────────────────────────────────────────
    /** Display string e.g. "1-1-1A" (location_display, not raw barcode). */
    location: string;
    /** Optional location-pin action (e.g. move a Drop session to a
     *  picker-selected location). Omitted renders the plain pin. */
    onLocationTap?: () => void;
    /** Disables the optional location-pin action while its parent is busy. */
    locationTapDisabled?: boolean;
    /** "high" | "low" — gates the bay diagram. */
    locationHighLow: "high" | "low" | null;
    /** "A1".."E6" or null. NULL → diagram is hidden. */
    bayPosition: string | null;

    /** Fallback identifier when the product has no barcode. */
    sku: string;
    productTitle: string | null;
    /** Shown in the product card AND as the action-bar scan target. */
    productBarcode: string | null;
    /** The product's SECOND barcode, shown under the primary barcode when
     *  present. NULL when the product has only one barcode (hero unchanged). */
    secondaryBarcode?: string | null;
    /** EVERY barcode the product scans as (primary first, extras after).
     *  When the set holds barcodes beyond the one the dark action bar
     *  shows, the bar gains a small yellow "+" badge and tapping the
     *  barcode area opens the alternate-barcodes popup. Absent/empty →
     *  no badge, bar renders as before. */
    productBarcodes?: string[] | null;
    productImageUrl: string | null;
    productThumbnailUrl: string | null;
    qtyTotal: number;
    qtyPicked: number;

    // ── Settings (resolved upstream by the container) ──────────────
    showProductImage: boolean;
    otherSkusToShow: number;

    // ── Data ───────────────────────────────────────────────────────
    otherSkusAtLocation: AlsoItem[];
    /** Location-level unit totals for the "Units at this location"
     *  counter — total units to pick at the current location across
     *  ALL SKUs in the run, and how many are picked so far. Driven
     *  from the pick-run data for the location (not the active line),
     *  so it moves on sibling-SKU picks too. Null/undefined (older BE
     *  payloads) hides the counter. */
    locationQtyTotal?: number | null;
    locationQtyPicked?: number | null;
    /** Distinct SKUs the run has at the current location — the counter
     *  only shows when > 1 (a single-SKU location's unit count is the
     *  hero counter already). */
    locationSkuCount?: number | null;
    /** Units still to pick per aisle pair across the whole run, in
     *  walk order, split by tunnel side where the floor has one (see
     *  aisleUnits.ts). Drives the "Units left by aisle" popup behind
     *  the route button in the top bar, so a picker blocked in an aisle
     *  can plan around it. Omitted/empty hides the button. */
    aisleUnits?: AisleGroup[] | null;
    /** Tap-to-jump from the aisle popup: called with the tapped row's
     *  location BARCODE; the container sends the same skip-to-location
     *  round-trip the location pin uses. Pass it only while a skip is
     *  allowed (same gate as `onLocationTap`); omitted → rows are plain
     *  text. */
    onAisleLocationSelect?: (location: string) => void;
    /** Holds the popup's rows while the container is busy. */
    aisleLocationSelectDisabled?: boolean;
    /** Warehouse the run is in. Not read by the screen itself today;
     *  kept on the props so the container and the aisle grouping
     *  (`aisleUnitsFor`, which takes it) agree on one source. */
    warehouse?: string | null;
    /** Items/hr from usePickSpeed. */
    pickSpeed: number;
    /** Trophy points for this run. Omitted, or `scoring: false`, when the
     *  warehouse hasn't set a target for the flow — the bar then looks exactly
     *  as it did before points arrived rather than showing a trophy on a screen
     *  that cannot earn one. Deliberately no ProgressBar: a picker on a Large
     *  order that legitimately takes minutes doesn't need a clock ticking
     *  down at them. */
    cycle?: CycleScoreApi;

    // ── Visual state ───────────────────────────────────────────────
    /** Which target should the green accent point at? */
    scanTarget: ScanTarget;

    // ── Action bar buttons ─────────────────────────────────────────
    onPause?: () => void;
    /** Missing-item / alert flow trigger. */
    onAlert?: () => void;
    onBack?: () => void;
    pauseDisabled?: boolean;
    backDisabled?: boolean;
    alertDisabled?: boolean;

    /** Manual pick ("barcode not scanning") — when set AND the scan
     *  target is the barcode, the dark action bar's scan glyph becomes
     *  a tappable hand button that opens the parent's manual-pick
     *  modal for the HERO item only. Omitted (or location target) →
     *  the plain ScanLine glyph renders as before. */
    onManualPick?: () => void;
    manualPickDisabled?: boolean;

    /** Pick-type label strip for the top bar (see PickingTopBar). */
    pickTypeLabel?: string;
    pickTypeKind?: string;
    /** Optional slot for module-specific extras in the top bar (e.g.
     *  open-tote pill for picking, filter pills for drop). Rendered
     *  between the meta pill and the icon buttons. */
    barExtras?: React.ReactNode;
    /** Optional slot rendered after the bay diagram, before the
     *  last-picked banner. Use this for the demo panel. (Named for the
     *  original layout where the action bar sat at the bottom.) */
    aboveActionBar?: React.ReactNode;
    /** Optional extra slot after aboveActionBar (rare; e.g. floating
     *  ticks). */
    belowActionBar?: React.ReactNode;

    /** The item the picker most recently picked (or dropped) — drives
     *  the green confirmation banner pinned to the bottom of the
     *  screen. With scan-any-item-at-location this is often NOT the
     *  hero line, which is exactly why the picker needs to see it.
     *  Null/undefined (before the first pick) hides the banner. */
    lastPicked?: LastPickedItem | null;
    /** Eyebrow label for the banner — defaults to "Last picked";
     *  the drop screens pass "Last dropped". */
    lastPickedLabel?: string;

    /** How long the full-screen location-change takeover stays up, in
     *  ms. Sourced from settings.picking_location_change_ms so the
     *  floor can tune it; falls back to LOCATION_CHANGE_MS. 0 skips
     *  the takeover (the highlight state still runs). */
    locationChangeMs?: number;
    /** How long the post-dismiss "new location" highlight holds, in
     *  ms. Sourced from settings.picking_new_location_highlight_ms;
     *  falls back to NEW_LOCATION_HIGHLIGHT_MS. 0 skips the highlight
     *  (the takeover still runs). */
    newLocationHighlightMs?: number;

    /** Bumped by the parent to suppress the location-change transition for the
     *  NEXT location change only — for a CORRECTIVE change, where the picker is
     *  being sent back somewhere rather than moving on. Drop uses it when a
     *  failed /drop/pick-complete rolls the hero back to the location the picker
     *  is still standing at: announcing "NEW LOCATION" and playing the success
     *  cue there would celebrate a move that never happened, on top of the
     *  failure's own error flash. Any change to this value arms the suppression;
     *  it disarms itself after the change it swallows. */
    suppressNextTransition?: number;

    /** True while the parent's bad-scan ScanFlashOverlay is showing.
     *  Blanks the content (location hero, action bar, product card,
     *  bay diagram, last-picked banner) so the red X reads against a
     *  clean page instead of popping over the location pin — only the
     *  progress + top bars stay put. Same treatment as the relabel
     *  page. */
    flashActive?: boolean;
}

/** What the green bottom banner shows — the identity of the unit the
 *  picker just picked, resolved by the container from whichever line
 *  the scan actually landed on (current line or a same-location
 *  sibling). */
export interface LastPickedItem {
    sku: string;
    title: string | null;
    barcode: string | null;
    imageUrl: string | null;
    thumbnailUrl: string | null;
}

// The warehouse-prefix strip for the hero's location code lives in
// locationDisplay.ts — the aisle-units card needs the same rule.

const PickingScreen = (props: PickingScreenProps) => {
    const { t } = useTranslation();

    const showBay = props.locationHighLow === "low" && !!props.bayPosition;
    const isLocationTarget = props.scanTarget === "location";

    // ── Alternate barcodes ────────────────────────────────────────
    // The additional barcodes = the full set minus whatever the dark
    // action bar already shows (primary barcode, or the SKU fallback),
    // de-duplicated with order preserved. Only when this is non-empty
    // does the bar grow its yellow "+" badge / tap affordance.
    const bannerBarcode = props.productBarcode || props.sku;
    const altBarcodes = (props.productBarcodes ?? []).filter(
        (bc, i, arr) => !!bc && bc !== bannerBarcode && arr.indexOf(bc) === i,
    );
    const [altOpen, setAltOpen] = useState(false);
    // Scans keep working under the popup (the keydown listener is on
    // the parent), so a pick can advance the hero while it's open —
    // close it then, or the list describes the wrong product.
    useEffect(() => { setAltOpen(false); }, [props.sku]);
    const showAltBadge = !isLocationTarget && altBarcodes.length > 0;

    // ── Units left by aisle ───────────────────────────────────────
    // Behind a top-bar button, not on the screen: it is route planning
    // for a picker who is stuck, so it must not crowd the scan-and-pick
    // eyeline. Scans keep working under the popup (listener is on the
    // parent); its numbers are live props, so it never goes stale.
    const hasAisles = !!props.aisleUnits && props.aisleUnits.length > 0;
    const [aislesOpen, setAislesOpen] = useState(false);
    useEffect(() => { if (!hasAisles) setAislesOpen(false); }, [hasAisles]);

    // ── Location-change transition ────────────────────────────────
    // Fires whenever the location prop CHANGES while mounted (never on
    // first mount — the picker hasn't walked anywhere yet). A run that
    // finishes unmounts this screen entirely, so no takeover can fire
    // after the last pick. The timer chain is cleared on unmount and
    // whenever a fresh change supersedes it.
    const [transition, setTransition] = useState<LocationTransition | null>(null);
    const heroRef = useRef<HTMLDivElement | null>(null);
    const prevLocationRef = useRef<string | null>(null);
    const timersRef = useRef<number[]>([]);
    const locationChangeMsRef = useRef(props.locationChangeMs);
    locationChangeMsRef.current = props.locationChangeMs;
    const highlightMsRef = useRef(props.newLocationHighlightMs);
    highlightMsRef.current = props.newLocationHighlightMs;

    // Suppression token for a corrective location change (see the prop docs).
    // Compared by value: when the parent has bumped it since the last location
    // change, that one change is swallowed and the token re-baselined, so a
    // genuine move straight afterwards still transitions normally.
    const suppressTokenRef = useRef(props.suppressNextTransition);

    useEffect(() => {
        const prev = prevLocationRef.current;
        prevLocationRef.current = props.location;
        if (prev === null || prev === props.location) return;

        // Corrective change — swallow it, and clear any transition already on
        // screen from the move being rolled back.
        if (suppressTokenRef.current !== props.suppressNextTransition) {
            suppressTokenRef.current = props.suppressNextTransition;
            timersRef.current.forEach(id => window.clearTimeout(id));
            timersRef.current = [];
            setTransition(null);
            return;
        }

        timersRef.current.forEach(id => window.clearTimeout(id));
        timersRef.current = [];

        // Location complete + takeover mount — the usual success cue.
        playSuccess();
        const takeoverMs  = locationChangeMsRef.current ?? LOCATION_CHANGE_MS;
        const highlightMs = highlightMsRef.current ?? NEW_LOCATION_HIGHLIGHT_MS;
        if (takeoverMs > 0) {
            // Measure where the location hero sits RIGHT NOW so the
            // takeover can draw the new code at the exact same viewport
            // position — the picker's eyes never have to jump between
            // the takeover and the screen it dismisses into.
            const heroRect = heroRef.current?.getBoundingClientRect();
            const heroTop = heroRect ? Math.max(0, heroRect.top) : undefined;
            setTransition({ stage: "takeover", prevLocation: prev, heroTop });
            timersRef.current.push(window.setTimeout(() => {
                setTransition(highlightMs > 0 ? { stage: "highlight", prevLocation: prev } : null);
            }, takeoverMs));
        } else if (highlightMs > 0) {
            setTransition({ stage: "highlight", prevLocation: prev });
        } else {
            setTransition(null);
        }
        if (highlightMs > 0) {
            timersRef.current.push(window.setTimeout(() => {
                setTransition(null);
            }, takeoverMs + highlightMs));
        }
    }, [props.location]);

    // Unmount (run complete, pause, navigation): kill the whole chain
    // so no orphaned timeout fires a takeover over another screen.
    useEffect(() => () => {
        timersRef.current.forEach(id => window.clearTimeout(id));
        timersRef.current = [];
    }, []);

    const isNewLocation = transition?.stage === "highlight";
    const wasLocation = transition ? stripWarehouse(transition.prevLocation) : "";

    return (
        <div className={"ps" + (props.flashActive ? " ps--flash" : "")}>
            <PickingTopBar
                priority={props.priority}
                itemsTotal={props.itemsTotal}
                itemsPicked={props.itemsPicked}
                pickTypeLabel={props.pickTypeLabel}
                pickTypeKind={props.pickTypeKind}
                barExtras={props.barExtras}
                onAisles={hasAisles ? () => setAislesOpen(true) : undefined}
                onPause={props.onPause}
                onAlert={props.onAlert}
                onBack={props.onBack}
                pauseDisabled={props.pauseDisabled}
                alertDisabled={props.alertDisabled}
                backDisabled={props.backDisabled}
            />

            <div className="ps-section">
                <div ref={heroRef} className={
                    "ps-loc-hero"
                    + (isLocationTarget ? " ps-loc-hero--scan-target" : "")
                    + (isNewLocation ? " ps-loc-hero--new" : "")
                }>
                    {props.onLocationTap ? (
                        <button
                            type="button"
                            className="ps-loc-pin ps-loc-pin--btn"
                            aria-label={t("picking_location_tap_label")}
                            onClick={props.onLocationTap}
                            disabled={props.locationTapDisabled}
                        >
                            <MapPin size={56} strokeWidth={1.75} />
                        </button>
                    ) : (
                        <span className="ps-loc-pin">
                            <MapPin size={56} strokeWidth={1.75} />
                        </span>
                    )}
                    {/* FitWord scales the location text down via
                        transform if its natural width exceeds the
                        available space — so a long aisle-bay-level
                        code never wraps or truncates. The wrapper is
                        keyed on the location so it REMOUNTS — and its
                        enter animation replays — on every location
                        change (the animation lives on the wrapper, not
                        FitWord itself, because FitWord owns an inline
                        transform for its scaling). */}
                    <span key={props.location} className="ps-loc-enter">
                        <FitWord className="ps-loc-num" align="left">{stripWarehouse(props.location)}</FitWord>
                    </span>
                </div>
                {/* Ribbon sits BELOW the hero (not above) so the hero
                    never shifts when the ribbon mounts — the location
                    code stays in the exact spot the takeover just
                    showed it in. */}
                {isNewLocation && (
                    <div className="ps-newloc-ribbon">
                        <span className="ps-newloc-ribbon-label">{t("picking_new_location_ribbon")}</span>
                        <span className="ps-newloc-ribbon-was">
                            {t("picking_new_location_was", { location: wasLocation })}
                        </span>
                    </div>
                )}
            </div>

            <div className={"ps-actionbar" + (!isLocationTarget ? " ps-actionbar--scan-target" : "")}>
                {/* Barcode target + a manual-pick handler → the glyph is
                    a button: tap to manually pick the hero item when its
                    barcode won't scan. Location target keeps the plain
                    scan glyph (manual pick never bypasses the location
                    scan). */}
                {props.onManualPick && !isLocationTarget ? (
                    <button
                        type="button"
                        className="ps-actionbar-icon ps-actionbar-manual"
                        onClick={props.onManualPick}
                        disabled={props.manualPickDisabled}
                        aria-label={t("picking_manual_btn")}
                        title={t("picking_manual_btn")}
                    >
                        <Hand size={24} strokeWidth={1.75} />
                    </button>
                ) : (
                    <div className="ps-actionbar-icon">
                        <ScanLine size={24} strokeWidth={1.75} />
                    </div>
                )}
                {showAltBadge ? (
                    /* The product has OTHER barcodes: the barcode area
                       becomes a button (everything in the bar except the
                       hand icon and the speed block) with a small yellow
                       "+" badge — tap to open the full list. */
                    <button
                        type="button"
                        className="ps-actionbar-text ps-actionbar-alt-btn"
                        onClick={() => setAltOpen(true)}
                        aria-label={t("picking_alt_barcodes_btn")}
                        title={t("picking_alt_barcodes_btn")}
                    >
                        <div className="ps-actionbar-target">
                            <FitWord align="left">{groupBarcode(bannerBarcode)}</FitWord>
                        </div>
                        <span className="ps-alt-badge" aria-hidden="true">
                            <Plus size={14} strokeWidth={3} />
                        </span>
                    </button>
                ) : (
                    <div className="ps-actionbar-text">
                        <div className="ps-actionbar-target">
                            {/* FitWord scales the value down if it would
                                overflow — so a long barcode/location is always
                                shown in full, as large as fits, never clipped. */}
                            <FitWord align="left">
                                {isLocationTarget ? stripWarehouse(props.location) : groupBarcode(props.productBarcode || props.sku)}
                            </FitWord>
                        </div>
                    </div>
                )}
                <div className="ps-actionbar-speed">
                    <div className="ps-actionbar-speed-label">{t("picking_pick_speed_label")}</div>
                    <div className="ps-actionbar-speed-val">
                        <Boxes size={14} style={{ marginRight: 4, opacity: 0.7 }} />
                        <span className="ps-n">{props.pickSpeed}</span>
                        <span className="ps-u">/hr</span>
                    </div>
                    {props.cycle?.scoring && (
                        <div className="ps-actionbar-points">
                            <ScoreDisplay total={props.cycle.total} />
                            <TimeBonusPopup
                                seconds={props.cycle.bonus.seconds}
                                color={props.cycle.bonus.color}
                                streak={props.cycle.bonus.streak}
                                isBest={props.cycle.bonus.isBest}
                                bumpKey={props.cycle.bonus.key}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="ps-divider" />

            <div className="ps-product">
                {props.showProductImage && (
                    <ProductImage
                        thumbnailUrl={props.productThumbnailUrl}
                        imageUrl={props.productImageUrl}
                    />
                )}
                <div className="ps-product-body">
                    {props.productTitle && (
                        <div className="ps-product-title">{props.productTitle}</div>
                    )}
                    {/* The primary barcode is NOT repeated here — the dark
                        action bar directly above already shows it as the
                        scan target. Only the product's SECOND barcode (when
                        it has one) renders, since it appears nowhere else. */}
                    {props.secondaryBarcode && (
                        <div className="ps-product-barcode2">{groupBarcode(props.secondaryBarcode)}</div>
                    )}
                </div>
                <span className="ps-qty" aria-label={t("picking_qty_remaining")}>
                    <span className="ps-qty-picked">{props.qtyPicked}</span>
                    <span className="ps-qty-slash">/</span>
                    <span className="ps-qty-total">{props.qtyTotal}</span>
                </span>
            </div>

            <AlsoAtLocation
                items={props.otherSkusAtLocation}
                max={props.otherSkusToShow}
                locationQtyTotal={props.locationQtyTotal}
                locationQtyPicked={props.locationQtyPicked}
                locationSkuCount={props.locationSkuCount}
            />

            {showBay && <BayDiagram bayPosition={props.bayPosition!} />}

            {props.aboveActionBar}

            {props.belowActionBar}

            {/* Green "last picked" confirmation, pinned to the bottom.
                Keyed on the SKU so the enter animation replays when the
                picked product CHANGES but not on every rescan of the
                same product (multi-qty lines). Reuses ProductImage —
                the .ps-lastpick descendant rules shrink it to 44px. */}
            {props.lastPicked && (
                <div key={props.lastPicked.sku} className="ps-lastpick" role="status">
                    <ProductImage
                        thumbnailUrl={props.lastPicked.thumbnailUrl}
                        imageUrl={props.lastPicked.imageUrl}
                    />
                    <div className="ps-lastpick-body">
                        <div className="ps-lastpick-eyebrow">
                            {props.lastPickedLabel ?? t("picking_last_picked_label")}
                        </div>
                        {props.lastPicked.title && (
                            <div className="ps-lastpick-title">{props.lastPicked.title}</div>
                        )}
                        <div className="ps-lastpick-barcode">
                            {groupBarcode(props.lastPicked.barcode || props.lastPicked.sku)}
                        </div>
                    </div>
                </div>
            )}

            {/* Alternate-barcodes popup — display-only, scans keep
                working underneath. Closed by the X / backdrop, or
                automatically when the hero product changes. */}
            {altOpen && altBarcodes.length > 0 && (
                <AlternateBarcodesModal
                    barcodes={altBarcodes}
                    onClose={() => setAltOpen(false)}
                />
            )}

            {/* "Units left by aisle" popup — opened from the top bar's
                route button; display-only, scans keep working under it. */}
            {aislesOpen && hasAisles && (
                <AisleSummary
                    groups={props.aisleUnits!}
                    onClose={() => setAislesOpen(false)}
                    onSelect={props.onAisleLocationSelect
                        ? (location) => { setAislesOpen(false); props.onAisleLocationSelect!(location); }
                        : undefined}
                    selectDisabled={props.aisleLocationSelectDisabled}
                />
            )}

            {/* Full-screen location-change takeover. Mounts over
                everything the moment the picker's line moves to a new
                location, auto-dismisses after locationChangeMs — the
                screen underneath is already on the next location. No
                tap needed (or possible): it's a moment, not a gate.
                The location row mirrors the on-screen .ps-loc-hero
                (same classes, pin size, alignment) and is pushed down
                to the hero's measured offset, so the code renders in
                the SAME spot as on the picking screen; the labels sit
                below it. */}
            {transition?.stage === "takeover" && (
                <div
                    className="ps-takeover"
                    role="status"
                    style={transition.heroTop !== undefined ? { paddingTop: transition.heroTop } : undefined}
                >
                    <div className="ps-loc-hero ps-takeover-hero">
                        <span className="ps-loc-pin">
                            <MapPin size={56} strokeWidth={1.75} />
                        </span>
                        <span className="ps-loc-enter">
                            <FitWord className="ps-loc-num" align="left">{stripWarehouse(props.location)}</FitWord>
                        </span>
                    </div>
                    <div className="ps-takeover-caption">
                        <div className="ps-takeover-eyebrow">{t("picking_new_location_title")}</div>
                        <div className="ps-takeover-was">
                            {t("picking_new_location_was", { location: wasLocation })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PickingScreen;
